import { useEffect, useRef, useState } from 'react';
import { modelAPI } from '../services/api';
import { ModelAssetDTO } from '../types';

// Admin-only page for curating the shared asset library. Gated server-side by
// the ADMIN_UPLOAD_TOKEN; this form just collects a .glb plus metadata and POSTs
// it to /models with the token. Not linked from the app — reach it at /admin.

const CATEGORIES = [
  'Characters',
  'Environment',
  'Structures',
  'Props',
  'Collectibles',
  'Blocks',
];

const BEHAVIORS: [string, string][] = [
  ['', 'None (static prop)'],
  ['player', 'Player'],
  ['collectible', 'Collectible'],
  ['goal', 'Goal'],
  ['obstacle', 'Obstacle'],
  ['hazard', 'Hazard'],
];

const TOKEN_KEY = 'yip_admin_token';

export default function AdminPage() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [token, setToken] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState('Characters');
  const [subcategory, setSubcategory] = useState('');
  const [defaultBehavior, setDefaultBehavior] = useState('');
  const [fitHeight, setFitHeight] = useState('');
  const [scale, setScale] = useState('1');
  const [animated, setAnimated] = useState(false);

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null
  );
  const [models, setModels] = useState<ModelAssetDTO[]>([]);

  // Remember the admin token locally so it isn't retyped every visit.
  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (saved) setToken(saved);
  }, []);

  const loadModels = async () => {
    try {
      const res = await modelAPI.getAll();
      setModels(res.data?.data ?? []);
    } catch {
      /* listing is best-effort */
    }
  };

  useEffect(() => {
    loadModels();
  }, []);

  const saveToken = (v: string) => {
    setToken(v);
    localStorage.setItem(TOKEN_KEY, v);
  };

  const canSubmit = !!token && !!file && !!label.trim() && !busy;

  const handleUpload = async () => {
    if (!canSubmit || !file) return;
    setBusy(true);
    setMessage(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', label.trim());
      fd.append('category', category);
      if (subcategory.trim()) fd.append('subcategory', subcategory.trim());
      if (defaultBehavior) fd.append('defaultBehavior', defaultBehavior);
      if (fitHeight.trim()) fd.append('fitHeight', fitHeight.trim());
      if (scale.trim()) fd.append('scale', scale.trim());
      fd.append('animated', String(animated));

      const res = await modelAPI.upload(fd, token);
      const created = res.data?.data;
      setMessage({
        kind: 'ok',
        text: `Uploaded "${created?.label ?? label}" to ${created?.category ?? category}.`,
      });
      // Reset the file + label for the next asset; keep category/token/etc.
      setFile(null);
      setLabel('');
      setSubcategory('');
      if (fileRef.current) fileRef.current.value = '';
      loadModels();
    } catch (err: any) {
      const text =
        err?.response?.data?.error?.message ||
        (err?.response?.status === 401
          ? 'Invalid admin token.'
          : 'Upload failed. Check the server logs and your R2 settings.');
      setMessage({ kind: 'err', text });
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white';
  const labelCls = 'block text-xs text-gray-400 mb-1';

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 py-10 px-4">
      <div className="max-w-xl mx-auto">
        <h1 className="text-2xl font-bold mb-1">Asset Library — Admin</h1>
        <p className="text-sm text-gray-400 mb-6">
          Upload curated <span className="text-gray-200">.glb</span> models into the
          shared library. These appear in every creator's palette.
        </p>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-5 space-y-4">
          <div>
            <label className={labelCls}>Admin token</label>
            <input
              type="password"
              value={token}
              onChange={(e) => saveToken(e.target.value)}
              placeholder="ADMIN_UPLOAD_TOKEN"
              className={field}
            />
          </div>

          <div>
            <label className={labelCls}>Model file (.glb)</label>
            <input
              ref={fileRef}
              type="file"
              accept=".glb"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-gray-700 file:text-gray-100"
            />
          </div>

          <div>
            <label className={labelCls}>Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Knight"
              className={field}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={field}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Subcategory (optional)</label>
              <input
                type="text"
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                placeholder="e.g. Heroes"
                className={field}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Default behavior</label>
              <select
                value={defaultBehavior}
                onChange={(e) => setDefaultBehavior(e.target.value)}
                className={field}
              >
                {BEHAVIORS.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2 pb-2">
              <input
                id="animated"
                type="checkbox"
                checked={animated}
                onChange={(e) => setAnimated(e.target.checked)}
              />
              <label htmlFor="animated" className="text-sm text-gray-300">
                Has animation
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Fit height (grid units, optional)</label>
              <input
                type="number"
                value={fitHeight}
                onChange={(e) => setFitHeight(e.target.value)}
                placeholder="1.8 = character, 4 = tree"
                className={field}
              />
            </div>
            <div>
              <label className={labelCls}>Scale</label>
              <input
                type="number"
                value={scale}
                onChange={(e) => setScale(e.target.value)}
                className={field}
              />
            </div>
          </div>

          <button
            onClick={handleUpload}
            disabled={!canSubmit}
            className={
              'w-full rounded px-4 py-2.5 text-sm font-semibold ' +
              (canSubmit
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed')
            }
          >
            {busy ? 'Uploading…' : 'Upload to library'}
          </button>

          {message && (
            <div
              className={
                'text-sm rounded px-3 py-2 ' +
                (message.kind === 'ok'
                  ? 'bg-emerald-900/30 border border-emerald-700/50 text-emerald-200'
                  : 'bg-red-900/30 border border-red-700/50 text-red-200')
              }
            >
              {message.text}
            </div>
          )}
        </div>

        <div className="mt-8">
          <h2 className="text-sm font-semibold text-gray-300 mb-2">
            In the library ({models.length})
          </h2>
          {models.length === 0 ? (
            <p className="text-xs text-gray-500">Nothing yet.</p>
          ) : (
            <ul className="space-y-1">
              {models.map((m) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between bg-gray-800/60 border border-gray-700 rounded px-3 py-2 text-sm"
                >
                  <span className="text-gray-200">{m.label}</span>
                  <span className="text-xs text-gray-500">
                    {m.category}
                    {m.subcategory ? ` · ${m.subcategory}` : ''}
                    {m.defaultBehavior ? ` · ${m.defaultBehavior}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
